import { MaterialIcons } from "@expo/vector-icons";
import { useCallback, useEffect, useRef, useState, } from "react";
import { Animated, Easing, StyleSheet, View, } from "react-native";
import { useCustomTheme } from "../hooks/useCustomTheme";
import { Button } from "./Button";
import { ThemedText } from "./ThemedText";
const getStatusStyles = (colors) => ({
    success: {
        borderColor: colors.success,
        backgroundColor: colors.palette.spinach90,
        iconColor: colors.success,
        iconName: "check-circle",
    },
    error: {
        borderColor: colors.danger,
        backgroundColor: colors.dangerLight,
        iconColor: colors.danger,
        iconName: "error",
    },
    warning: {
        borderColor: colors.warning,
        backgroundColor: colors.palette.banana90,
        iconColor: colors.warning,
        iconName: "warning",
    },
});
export const StatusBanner = ({ type, message, onViewPress, viewButtonLabel, dismissible, dismissButtonLabel, onDismiss, dismissSpacingCompensation = 0, autoDismissAfterSeconds, style, }) => {
    const { colors, fonts } = useCustomTheme();
    const [isVisible, setIsVisible] = useState(true);
    const [isDismissing, setIsDismissing] = useState(false);
    const [contentHeight, setContentHeight] = useState(null);
    const showViewAction = Boolean(onViewPress) && type === "success";
    const hasActions = showViewAction || dismissible;
    const opacity = useRef(new Animated.Value(1)).current;
    const collapseHeight = useRef(new Animated.Value(0)).current;
    const collapseSpacing = useRef(new Animated.Value(0)).current;
    const statusStyle = getStatusStyles(colors)[type];
    useEffect(() => {
        if (contentHeight === null || isDismissing) {
            return;
        }
        collapseHeight.setValue(contentHeight);
    }, [collapseHeight, contentHeight, isDismissing]);
    const onDismissPress = useCallback(() => {
        if (isDismissing) {
            return;
        }
        setIsDismissing(true);
        Animated.parallel([
            Animated.timing(collapseHeight, {
                toValue: 0,
                duration: 220,
                easing: Easing.out(Easing.ease),
                useNativeDriver: false,
            }),
            Animated.timing(collapseSpacing, {
                toValue: -dismissSpacingCompensation,
                duration: 220,
                easing: Easing.out(Easing.ease),
                useNativeDriver: false,
            }),
            Animated.timing(opacity, {
                toValue: 0,
                duration: 220,
                easing: Easing.out(Easing.ease),
                useNativeDriver: true,
            }),
        ]).start(({ finished }) => {
            if (!finished) {
                return;
            }
            setIsVisible(false);
            onDismiss?.();
        });
    }, [
        collapseHeight,
        collapseSpacing,
        dismissSpacingCompensation,
        isDismissing,
        onDismiss,
        opacity,
    ]);
    useEffect(() => () => {
        collapseHeight.stopAnimation();
        collapseSpacing.stopAnimation();
        opacity.stopAnimation();
    }, [collapseHeight, collapseSpacing, opacity]);
    useEffect(() => {
        if (autoDismissAfterSeconds === undefined ||
            autoDismissAfterSeconds <= 0 ||
            isDismissing ||
            !isVisible) {
            return;
        }
        const timeout = setTimeout(() => {
            onDismissPress();
        }, autoDismissAfterSeconds * 1000);
        return () => clearTimeout(timeout);
    }, [autoDismissAfterSeconds, isDismissing, isVisible, onDismissPress]);
    if (!isVisible) {
        return null;
    }
    return (<View style={styles.wrapper}>
      <Animated.View onLayout={({ nativeEvent }) => {
            const measuredHeight = nativeEvent.layout.height;
            if (measuredHeight > 0 && measuredHeight !== contentHeight) {
                setContentHeight(measuredHeight);
            }
        }} style={[
            styles.collapsibleContainer,
            isDismissing && contentHeight !== null
                ? [
                    styles.collapsibleContainerDismissing,
                    { height: collapseHeight },
                ]
                : null,
            isDismissing ? { marginBottom: collapseSpacing } : null,
            style,
        ]}>
        <Animated.View style={[
            styles.container,
            hasActions && styles.containerWithActions,
            {
                borderColor: statusStyle.borderColor,
                backgroundColor: statusStyle.backgroundColor,
            },
            { opacity },
        ]}>
          <View style={styles.contentContainer}>
            <MaterialIcons name={statusStyle.iconName} size={24} color={statusStyle.iconColor}/>
            <ThemedText type="default" style={[styles.messageText, fonts.bold]}>
              {message}
            </ThemedText>
          </View>
          {hasActions && (<View style={styles.actionsContainer}>
              {showViewAction && (<Button buttonText={viewButtonLabel} iconName="eye" iconPosition="left" iconSize={24} onPress={onViewPress} iconColor={colors.text} buttonStyle={styles.actionButton} buttonTextStyle={[
                    styles.actionButtonText,
                    { color: colors.text, ...fonts.bold },
                ]}/>)}
              {dismissible && (<Button buttonText={dismissButtonLabel} iconName="close" iconPosition="left" iconSize={24} onPress={onDismissPress} disabled={isDismissing} iconColor={colors.text} buttonStyle={styles.actionButton} buttonTextStyle={[
                    styles.actionButtonText,
                    { color: colors.text, ...fonts.bold },
                ]}/>)}
            </View>)}
        </Animated.View>
      </Animated.View>
    </View>);
};
const styles = StyleSheet.create({
    wrapper: {
        width: "100%",
        alignSelf: "stretch",
    },
    collapsibleContainer: {
        width: "100%",
        alignSelf: "stretch",
    },
    collapsibleContainerDismissing: {
        overflow: "hidden",
    },
    container: {
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        borderWidth: 1,
        borderRadius: 28,
        paddingHorizontal: 16,
        paddingVertical: 12,
        height: 58,
        boxShadow: "0px 4px 12px 0px #00000014",
        elevation: 3,
    },
    containerWithActions: {
        justifyContent: "space-between",
        minHeight: 58,
        height: "auto",
        paddingVertical: 8,
    },
    contentContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: 16,
        flex: 1,
        minWidth: 0,
    },
    messageText: {
        lineHeight: 20.8,
    },
    actionsContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 16,
    },
    actionButton: {
        minWidth: 0,
        height: 24,
        borderRadius: 0,
        paddingHorizontal: 0,
        gap: 0,
        backgroundColor: "transparent",
        borderWidth: 0,
    },
    actionButtonText: {
        flexGrow: 0,
        fontSize: 16,
        lineHeight: 20.8,
        fontWeight: "700",
        paddingHorizontal: 8,
    },
});
//# sourceMappingURL=StatusBanner.js.map